'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';

import { useState, type JSX } from 'react';

import { cn, getInitials } from '~/utils';
import { useNotifications } from '~/hooks/useNotifications';
import {
  Show,
  HStack,
  Avatar,
  Button,
  LogoIcon,
  AvatarImage,
  AvatarFallback,
} from '~/components';

import { Nav } from './Nav';
import { ChatDropdown } from './ChatDropdown';
import { HamburgerDropdown } from './HamburgerDropdown';
import { NotificationDropdown } from './NotificationDropdown';

function RightContent(): JSX.Element {
  const { data: session } = useSession();
  const [isNotiOpen, setIsNotiOpen] = useState<boolean>(false);
  const { notifications, unreadNotifications } = useNotifications();

  return (
    <>
      <Show when={session}>
        <HStack gap={4} className='items-center'>
          <ChatDropdown />
          <NotificationDropdown
            open={isNotiOpen}
            setOpen={setIsNotiOpen}
            notifications={notifications}
          />
          <Avatar>
            <AvatarImage src={session?.user.avatar} />
            <AvatarFallback>
              {getInitials(session?.user.fullName)}
            </AvatarFallback>
          </Avatar>
        </HStack>
      </Show>
      <HamburgerDropdown
        onNotiClick={() => setIsNotiOpen(true)}
        unreadNotiCount={unreadNotifications.length}
      />
    </>
  );
}

export function Header(): JSX.Element {
  const t = useTranslations();
  const { data: session } = useSession();
  const pathname = usePathname();

  return (
    <div className='py-4 fixed w-full top-0 border-[#ececec] border-b-1 bg-white z-1000'>
      <div
        className={cn(
          'px-6 flex justify-between items-center gap-0 max-w-[1440px] mx-auto',
          'xl:px-20 xl:gap-2.5',
        )}
      >
        <div className={'w-max flex items-center shrink-0'}>
          <Link href='/'>
            <LogoIcon w='97px' h='30px' />
          </Link>
        </div>
        <div className={cn('hidden', 'md:block')}>
          <Nav />
        </div>
        <div className={'flex items-center gap-4 w-max shrink-0 justify-end'}>
          <Show when={!session}>
            <Link
              href='/sign-up'
              className={cn('hidden font-bold', 'xl:block')}
            >
              {t('commons.signUp')}
            </Link>
            <Button
              asChild
              variant='outline'
              className={cn(
                'hidden min-w-max py-[8.5px] h-[38px] text-center',
                'xl:block xl:min-w-[120px]',
              )}
            >
              <Link
                href={{
                  pathname: '/sign-in',
                  query: {
                    callbackUrl: pathname,
                  },
                }}
              >
                {t('commons.logIn')}
              </Link>
            </Button>
          </Show>
          <RightContent />
        </div>
      </div>
    </div>
  );
}
